import { motion } from 'motion/react'
import {
  Activity,
  AlertTriangle,
  Bell,
  Boxes,
  Cable,
  CalendarClock,
  ChartNoAxesCombined,
  CheckCircle2,
  Clock3,
  CloudCog,
  Cpu,
  Eye,
  Gauge,
  KeyRound,
  LineChart,
  MonitorCheck,
  Network,
  Radar,
  RadioTower,
  Router,
  ShieldCheck,
  Signal,
  SlidersHorizontal,
  TrendingUp,
  Wrench,
} from 'lucide-react'
import { cn } from '@/lib/utils'

type ServiceType = 'connect' | 'analytics' | 'action'

interface ServiceHeroVisualProps {
  service: ServiceType
}

/**
 * Shared hero visual component for service pages.
 * Renders a polished, animated visual that communicates the service value proposition.
 */
export function ServiceHeroVisual({ service }: ServiceHeroVisualProps) {
  return (
    <motion.div
      className="relative hidden lg:block"
      initial={{ opacity: 0, scale: 0.97, y: 20 }}
      animate={{ opacity: 1, scale: 1, y: 0 }}
      transition={{ duration: 0.65, delay: 0.25, ease: 'easeOut' }}
    >
      {/* Glow behind card */}
      <div className="absolute -inset-8 rounded-[2rem] bg-accent/10 blur-[70px]" aria-hidden />

      {/* Glass card container */}
      <div className="relative overflow-hidden rounded-2xl border border-border bg-card/90 shadow-2xl shadow-primary/10 backdrop-blur-sm dark:border-white/10 dark:bg-white/[0.035] dark:shadow-black/35">
        {service === 'connect' && <ConnectVisual />}
        {service === 'analytics' && <AnalyticsVisual />}
        {service === 'action' && <ActionVisual />}
      </div>
    </motion.div>
  )
}

/* ─── Data Ingestion: Network Topology / Data Flow ─── */
function ConnectVisual() {
  const nodes = [
    { icon: Cpu,                 label: 'Devices',      x: '5%',  y: '50%' },
    { icon: RadioTower,          label: 'Edge',          x: '20%', y: '50%' },
    { icon: Signal,              label: 'Broker',        x: '36%', y: '50%' },
    { icon: Activity,            label: 'Processing',    x: '52%', y: '50%' },
    { icon: CloudCog,            label: 'Storage',       x: '67%', y: '18%' },
    { icon: ChartNoAxesCombined, label: 'Analytics',     x: '83%', y: '33%' },
    { icon: Boxes,               label: 'Applications',  x: '79%', y: '75%' },
  ]

  // Proportional keyframe timing so the particle moves at constant speed.
  // Segment lengths: 15, 16, 16, 35.3, 21.9, 42.2  →  total ≈ 146.4
  const mainTimes: number[] = [0, 0.10, 0.21, 0.32, 0.56, 0.71, 1.0]
  const nodeGlowDelays = mainTimes.map((time) => (time >= 1 ? 7.55 : time * 8))

  return (
    <div className="p-6">
      {/* Header */}
      <div className="mb-6 flex items-center justify-between gap-3">
        <div>
          <p className="text-xs font-semibold uppercase tracking-[0.18em] text-muted-foreground">
            Data Pipeline
          </p>
          <h2 className="mt-1 text-xl font-semibold text-foreground">Data ingestion topology</h2>
        </div>
        <span className="rounded-full border border-accent/30 bg-accent/10 px-3 py-1 text-[10px] font-semibold uppercase tracking-[0.14em] text-accent">
          Live
        </span>
      </div>

      <div className="relative h-72 w-full">
        {/* SVG lines stay behind opaque nodes, so connectors read as module-to-module links. */}
        <svg className="absolute inset-0 z-0 h-full w-full" aria-hidden>
          {/* Horizontal spine */}
          <line x1="5%"  y1="50%" x2="20%" y2="50%" stroke="oklch(68% .095 52 / 0.4)" strokeWidth="1.5" strokeDasharray="4 3" />
          <line x1="20%" y1="50%" x2="36%" y2="50%" stroke="oklch(68% .095 52 / 0.4)" strokeWidth="1.5" strokeDasharray="4 3" />
          <line x1="36%" y1="50%" x2="52%" y2="50%" stroke="oklch(68% .095 52 / 0.4)" strokeWidth="1.5" strokeDasharray="4 3" />
          {/* Processing → Storage */}
          <line x1="52%" y1="50%" x2="67%" y2="18%" stroke="oklch(68% .095 52 / 0.5)" strokeWidth="1.5" />
          {/* Storage → Analytics */}
          <line x1="67%" y1="18%" x2="83%" y2="33%" stroke="oklch(68% .095 52 / 0.5)" strokeWidth="1.5" />
          {/* Analytics → Applications */}
          <line x1="83%" y1="33%" x2="79%" y2="75%" stroke="oklch(68% .095 52 / 0.45)" strokeWidth="1.5" />
          {/* Processing → Applications direct shortcut */}
          <line x1="52%" y1="50%" x2="79%" y2="75%" stroke="oklch(68% .095 52 / 0.18)" strokeWidth="1" strokeDasharray="3 4" />
        </svg>

        {/* Particles travel behind the nodes. */}
        <motion.div
          className="absolute z-[1] h-2.5 w-2.5 -translate-x-1/2 -translate-y-1/2 rounded-full bg-accent"
          style={{ boxShadow: '0 0 7px 2px oklch(68% .095 52 / 0.55)' }}
          animate={{
            left: ['5%',  '20%', '36%', '52%', '67%', '83%', '79%'],
            top:  ['50%', '50%', '50%', '50%', '18%', '33%', '75%'],
          }}
          transition={{ duration: 8, repeat: Infinity, ease: 'linear', times: mainTimes }}
        />
        <motion.div
          className="absolute z-[1] h-1.5 w-1.5 -translate-x-1/2 -translate-y-1/2 rounded-full bg-accent/60"
          animate={{ left: ['52%', '79%'], top: ['50%', '75%'] }}
          transition={{ duration: 3.5, repeat: Infinity, ease: 'linear', delay: 4 }}
        />

        {/* Nodes mask the connector layer so lines never cut through icons. */}
        {nodes.map((node, i) => {
          const glowDelay = nodeGlowDelays[i]

          return (
            <motion.div
              key={node.label}
              className="absolute z-20 -translate-x-1/2 -translate-y-1/2"
              style={{ left: node.x, top: node.y }}
              initial={{ opacity: 0, scale: 0.8 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.4, delay: 0.3 + i * 0.07 }}
            >
              <motion.span
                aria-hidden
                className="absolute -inset-3 rounded-2xl bg-accent/35 blur-md"
                initial={{ opacity: 0, scale: 0.75 }}
                animate={{ opacity: [0, 0.72, 0], scale: [0.76, 1.12, 1.42] }}
                transition={{
                  duration: 0.9,
                  repeat: Infinity,
                  repeatDelay: 7.1,
                  delay: glowDelay,
                  ease: 'easeOut',
                }}
              />
              <motion.div
                className="relative flex h-10 w-10 items-center justify-center rounded-xl border border-accent/30 bg-card shadow-lg shadow-accent/10 ring-4 ring-card dark:bg-[#2b2a25] dark:ring-[#23231f]"
                animate={{ filter: ['brightness(1)', 'brightness(1.18)', 'brightness(1)'] }}
                transition={{
                  duration: 0.9,
                  repeat: Infinity,
                  repeatDelay: 7.1,
                  delay: glowDelay,
                  ease: 'easeOut',
                }}
              >
                <node.icon className="h-4 w-4 text-accent" />
                <span className="absolute left-1/2 top-full mt-2 -translate-x-1/2 whitespace-nowrap text-[10px] font-medium text-muted-foreground">
                  {node.label}
                </span>
              </motion.div>
            </motion.div>
          )
        })}
      </div>

      {/* Stats bar */}
      <div className="mt-4 grid grid-cols-3 gap-3">
        {[
          { label: 'Protocols', value: '12' },
          { label: 'Devices', value: '2.4k' },
          { label: 'Uptime', value: '99.97%' },
        ].map((stat) => (
          <div
            key={stat.label}
            className="rounded-lg border border-border/60 bg-background/50 p-3 text-center dark:border-white/[0.06] dark:bg-white/[0.02]"
          >
            <p className="text-lg font-semibold text-foreground">{stat.value}</p>
            <p className="text-[10px] uppercase tracking-[0.12em] text-muted-foreground">
              {stat.label}
            </p>
          </div>
        ))}
      </div>
    </div>
  )
}

/* ─── Data Processing & Analytics: Processing Workspace ─── */
function AnalyticsVisual() {
  const barHeights = [35, 48, 42, 68, 57, 72, 64, 83, 78, 92, 70, 55]

  return (
    <div className="p-6">
      {/* Header */}
      <div className="mb-6 flex items-center justify-between gap-3">
        <div>
          <p className="text-xs font-semibold uppercase tracking-[0.18em] text-muted-foreground">
            Analytics Layer
          </p>
          <h2 className="mt-1 text-xl font-semibold text-foreground">Processing workspace</h2>
        </div>
        <span className="rounded-full border border-accent/30 bg-accent/10 px-3 py-1 text-[10px] font-semibold uppercase tracking-[0.14em] text-accent">
          Live
        </span>
      </div>

      {/* KPI row */}
      <div className="mb-4 grid grid-cols-3 gap-3">
        {[
          { label: 'Normalized', value: '1.8M', tone: 'text-accent' },
          { label: 'Rules Active', value: '42', tone: 'text-foreground' },
          { label: 'Data Quality', value: '98%', tone: 'text-primary' },
        ].map((kpi) => (
          <div
            key={kpi.label}
            className="rounded-xl border border-border/60 bg-background/50 p-3 dark:border-white/[0.06] dark:bg-white/[0.02]"
          >
            <p className="text-[10px] uppercase tracking-[0.12em] text-muted-foreground">
              {kpi.label}
            </p>
            <p className={cn('mt-1.5 text-2xl font-semibold', kpi.tone)}>{kpi.value}</p>
          </div>
        ))}
      </div>

      {/* Chart + Health panel */}
      <div className="grid gap-3 lg:grid-cols-[1.4fr_0.9fr]">
        {/* Telemetry trend chart */}
        <div className="rounded-xl border border-border/60 bg-background/50 p-4 dark:border-white/[0.06] dark:bg-white/[0.02]">
          <div className="mb-4 flex items-center justify-between">
            <span className="text-xs font-semibold text-muted-foreground">Signal processing</span>
            <LineChart className="h-4 w-4 text-accent" />
          </div>
          <div className="flex h-32 items-end gap-1.5">
            {barHeights.map((height, index) => (
              <motion.div
                key={index}
                className="flex flex-1 items-end"
                initial={{ height: 0 }}
                animate={{ height: `${height}%` }}
                transition={{
                  duration: 0.6,
                  delay: 0.4 + index * 0.05,
                  ease: 'easeOut',
                }}
              >
                <div
                  className="w-full rounded-t-sm bg-gradient-to-t from-primary/35 via-accent/60 to-accent"
                  style={{ height: '100%' }}
                />
              </motion.div>
            ))}
          </div>
        </div>

        {/* Asset health */}
        <div className="rounded-xl border border-border/60 bg-background/50 p-4 dark:border-white/[0.06] dark:bg-white/[0.02]">
          <div className="mb-4 flex items-center justify-between">
            <span className="text-xs font-semibold text-muted-foreground">Data Quality</span>
            <Radar className="h-4 w-4 text-accent" />
          </div>
          <div className="space-y-3">
            {[
              ['Clean', '86%'],
              ['Watch', '10%'],
              ['Review', '4%'],
            ].map(([label, value]) => (
              <div key={label}>
                <div className="mb-1 flex justify-between text-xs text-muted-foreground">
                  <span>{label}</span>
                  <span>{value}</span>
                </div>
                <div className="h-2 overflow-hidden rounded-full bg-muted">
                  <motion.div
                    className="h-full rounded-full bg-accent"
                    initial={{ width: 0 }}
                    animate={{ width: value }}
                    transition={{ duration: 0.8, delay: 0.6, ease: 'easeOut' }}
                  />
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  )
}

/* ─── Action Intelligence & Automation: Action Workflow / Risk Profile ─── */
function ActionVisual() {
  const riskSignals = [
    { label: 'Temperature Drift', value: 68, status: 'watch' },
    { label: 'Pressure Variance', value: 42, status: 'stable' },
    { label: 'Runtime Pattern', value: 81, status: 'risk' },
    { label: 'Service Window', value: 56, status: 'watch' },
  ]

  return (
    <div className="p-6">
      {/* Header */}
      <div className="mb-6 flex items-center justify-between gap-3">
        <div>
          <p className="text-xs font-semibold uppercase tracking-[0.18em] text-muted-foreground">
            Action Layer
          </p>
          <h2 className="mt-1 text-xl font-semibold text-foreground">Action workflow</h2>
        </div>
        <span className="rounded-full border border-accent/30 bg-accent/10 px-3 py-1 text-[10px] font-semibold uppercase tracking-[0.14em] text-accent">
          Watch
        </span>
      </div>

      {/* Health score + signals */}
      <div className="mb-3 grid gap-3 lg:grid-cols-[0.82fr_1.18fr]">
        {/* Health score */}
        <div className="rounded-xl border border-border/60 bg-background/50 p-4 dark:border-white/[0.06] dark:bg-white/[0.02]">
          <p className="text-[10px] uppercase tracking-[0.12em] text-muted-foreground">
            Health Score
          </p>
          <motion.div
            className="mt-4 flex items-end gap-2"
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.3 }}
          >
            <span className="text-6xl font-semibold text-accent">72</span>
            <span className="pb-2 text-sm text-muted-foreground">/100</span>
          </motion.div>
          <motion.p
            className="mt-4 text-sm leading-6 text-muted-foreground"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.5, delay: 0.5 }}
          >
            Runtime variance and temperature drift crossed the configured automation threshold.
          </motion.p>
        </div>

        {/* Leading signals */}
        <div className="rounded-xl border border-border/60 bg-background/50 p-4 dark:border-white/[0.06] dark:bg-white/[0.02]">
          <div className="mb-4 flex items-center justify-between">
            <span className="text-xs font-semibold text-muted-foreground">Leading signals</span>
            <LineChart className="h-4 w-4 text-accent" />
          </div>
          <div className="space-y-4">
            {riskSignals.map((signal, index) => (
              <motion.div
                key={signal.label}
                initial={{ opacity: 0, x: -10 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.4, delay: 0.4 + index * 0.1 }}
              >
                <div className="mb-1.5 flex justify-between text-xs text-muted-foreground">
                  <span>{signal.label}</span>
                  <span className="capitalize">{signal.status}</span>
                </div>
                <div className="h-2 overflow-hidden rounded-full bg-muted">
                  <motion.div
                    className="h-full rounded-full bg-accent"
                    initial={{ width: 0 }}
                    animate={{ width: `${signal.value}%` }}
                    transition={{ duration: 0.8, delay: 0.5 + index * 0.1, ease: 'easeOut' }}
                  />
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </div>

      {/* Recommendation */}
      <motion.div
        className="rounded-xl border border-accent/20 bg-accent/[0.07] p-4"
        initial={{ opacity: 0, y: 8 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5, delay: 0.7 }}
      >
        <div className="flex items-start gap-3">
          <Clock3 className="mt-0.5 h-4 w-4 shrink-0 text-accent" />
          <p className="text-sm leading-6 text-foreground">
            Recommended action: create a service case and inspect bearing assembly during the next planned service window.
          </p>
        </div>
      </motion.div>
    </div>
  )
}
